import { 
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Category } from '../models/Category';
import { FormItem } from '../models/FormItem';
import { Scheme } from '../models/Scheme';

export class FirestoreService {
  async getCategories(): Promise<Category[]> {
    const snapshot = await getDocs(collection(db, 'categories'));
    const categories = snapshot.docs.map(doc => {
      const data = doc.data();
      
      let nameValue = '';
      let nameMap: Record<string, string> | undefined;
      
      if (typeof data.name === 'object' && data.name !== null) {
        nameMap = data.name as Record<string, string>;
        nameValue = data.name.en || data.name.hi || doc.id;
      } else {
        nameValue = String(data.name || doc.id);
      }
      
      return {
        id: doc.id,
        name: nameValue,
        icon: data.icon || 'category',
        priority: data.priority || 0,
        _rawNameMap: nameMap
      } as Category;
    });

    return categories.sort((a, b) => a.priority - b.priority);
  }

  async getCategoryById(id: string): Promise<Category | null> {
    const docRef = doc(db, 'categories', id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      
      let nameValue = '';
      let nameMap: Record<string, string> | undefined;
      
      if (typeof data.name === 'object' && data.name !== null) {
        nameMap = data.name as Record<string, string>;
        nameValue = data.name.en || data.name.hi || docSnap.id;
      } else {
        nameValue = String(data.name || docSnap.id);
      }
      
      return {
        id: docSnap.id,
        name: nameValue,
        icon: data.icon || 'category',
        priority: data.priority || 0,
        _rawNameMap: nameMap
      } as Category;
    }
    return null;
  }

  private mapFormData(docId: string, data: any): FormItem {
    let nameValue = '';
    let nameMap: Record<string, string> | undefined;
    
    if (typeof data.name === 'object' && data.name !== null) {
      nameMap = data.name as Record<string, string>;
      nameValue = data.name.en || data.name.hi || docId;
    } else {
      nameValue = String(data.name || docId);
    }
    
    let shortDescValue = '';
    let shortDescMap: Record<string, string> | undefined;
    
    if (typeof data.shortDescription === 'object' && data.shortDescription !== null) {
      shortDescMap = data.shortDescription as Record<string, string>;
      shortDescValue = data.shortDescription.en || data.shortDescription.hi || '';
    } else {
      shortDescValue = String(data.shortDescription || '');
    }
    
    let whoCanApplyValue = '';
    let whoCanApplyMap: Record<string, string> | undefined;
    
    if (typeof data.whoCanApply === 'object' && data.whoCanApply !== null) {
      whoCanApplyMap = data.whoCanApply as Record<string, string>;
      whoCanApplyValue = data.whoCanApply.en || data.whoCanApply.hi || '';
    } else {
      whoCanApplyValue = String(data.whoCanApply || '');
    }
    
    return {
      id: docId,
      categoryId: data.categoryId || '',
      name: nameValue,
      shortDescription: shortDescValue,
      whoCanApply: whoCanApplyValue,
      applicableStates: data.applicableStates || [],
      portalLink: data.portalLink || '',
      formPdfPath: data.formPdfPath,
      requiredDocuments: Array.isArray(data.requiredDocuments) 
        ? data.requiredDocuments 
        : [],
      steps: Array.isArray(data.steps) ? data.steps : [],
      commonMistakes: Array.isArray(data.commonMistakes) ? data.commonMistakes : [],
      requiredLinks: Array.isArray(data.requiredLinks) ? data.requiredLinks : [],
      isActive: data.isActive !== false,
      popularity: data.popularity || 0,
      priority: data.priority || 0,
      adsAllowed: data.adsAllowed !== false,
      isSensitive: data.isSensitive === true,
      _rawNameMap: nameMap,
      _rawShortDescMap: shortDescMap,
      _rawWhoCanApplyMap: whoCanApplyMap,
      _rawRequiredDocuments: data.requiredDocuments,
      _rawSteps: data.steps,
      _rawCommonMistakes: data.commonMistakes
    };
  }

  async getAllForms(): Promise<FormItem[]> {
    const snapshot = await getDocs(collection(db, 'forms'));
    return snapshot.docs.map(doc => this.mapFormData(doc.id, doc.data()));
  }

  async getFormsByCategory(categoryId: string): Promise<FormItem[]> {
    const q = query(
      collection(db, 'forms'),
      where('categoryId', '==', categoryId),
      where('isActive', '==', true)
    );
    const snapshot = await getDocs(q);
    const forms = snapshot.docs.map(doc => this.mapFormData(doc.id, doc.data()));
    return forms.sort((a, b) => a.priority - b.priority);
  }

  async getFormById(id: string): Promise<FormItem | null> {
    const docRef = doc(db, 'forms', id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return this.mapFormData(docSnap.id, docSnap.data());
    }
    return null;
  }

  async getActiveSchemes(): Promise<Scheme[]> {
    try {
      const q = query(
        collection(db, 'schemes'),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(q);
      const schemes = snapshot.docs.map(doc => {
        const data = doc.data();
      
      let titleValue = '';
      let titleMap: Record<string, string> | undefined;
      
      if (typeof data.title === 'object' && data.title !== null) {
        titleMap = data.title as Record<string, string>;
        titleValue = data.title.en || data.title.hi || doc.id;
      } else {
        titleValue = String(data.title || doc.id);
      }
      
      let descValue = '';
      let descMap: Record<string, string> | undefined;
      
      if (typeof data.description === 'object' && data.description !== null) {
        descMap = data.description as Record<string, string>;
        descValue = data.description.en || data.description.hi || '';
      } else {
        descValue = String(data.description || '');
      }
      
      let eligibilityValue = '';
      let eligibilityMap: Record<string, string> | undefined;
      
      if (typeof data.eligibility === 'object' && data.eligibility !== null) {
        eligibilityMap = data.eligibility as Record<string, string>;
        eligibilityValue = data.eligibility.en || data.eligibility.hi || '';
      } else {
        eligibilityValue = String(data.eligibility || '');
      }
      
      let procedureValue = '';
      let procedureMap: Record<string, string> | undefined;
      
      if (typeof data.procedure === 'object' && data.procedure !== null) {
        procedureMap = data.procedure as Record<string, string>;
        procedureValue = data.procedure.en || data.procedure.hi || '';
      } else {
        procedureValue = String(data.procedure || '');
      }
      
      let benefitsValue = '';
      let benefitsMap: Record<string, string> | undefined;
      
      if (typeof data.benefits === 'object' && data.benefits !== null) {
        benefitsMap = data.benefits as Record<string, string>;
        benefitsValue = data.benefits.en || data.benefits.hi || '';
      } else {
        benefitsValue = String(data.benefits || '');
      }
      
      return {
        id: doc.id,
        category: data.category,
        title: titleValue,
        description: descValue,
        eligibility: eligibilityValue,
        procedure: procedureValue,
        benefits: benefitsValue,
        officialLink: data.officialLink || '',
        launchDate: data.launchDate?.toDate(),
        lastUpdated: data.lastUpdated?.toDate(),
        isActive: data.isActive !== false,
        priority: data.priority || 0,
        stateAvailability: data.stateAvailability || [],
        isNew: data.isNew || false,
        _rawTitleMap: titleMap,
        _rawDescriptionMap: descMap,
        _rawEligibilityMap: eligibilityMap,
        _rawProcedureMap: procedureMap,
        _rawBenefitsMap: benefitsMap
      } as Scheme;
    });
    
    // Sort by priority in memory to avoid requiring a Firestore composite index
    return schemes.sort((a, b) => a.priority - b.priority);
    } catch (error) {
      console.error('Error fetching schemes:', error);
      return [];
    }
  }

  async getSchemeById(id: string): Promise<Scheme | null> {
    const docRef = doc(db, 'schemes', id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      
      let titleValue = '';
      let titleMap: Record<string, string> | undefined;
      
      if (typeof data.title === 'object' && data.title !== null) {
        titleMap = data.title as Record<string, string>;
        titleValue = data.title.en || data.title.hi || docSnap.id;
      } else {
        titleValue = String(data.title || docSnap.id);
      }
      
      let descValue = '';
      let descMap: Record<string, string> | undefined;
      
      if (typeof data.description === 'object' && data.description !== null) {
        descMap = data.description as Record<string, string>;
        descValue = data.description.en || data.description.hi || '';
      } else {
        descValue = String(data.description || '');
      }
      
      let eligibilityValue = '';
      let eligibilityMap: Record<string, string> | undefined;
      
      if (typeof data.eligibility === 'object' && data.eligibility !== null) {
        eligibilityMap = data.eligibility as Record<string, string>;
        eligibilityValue = data.eligibility.en || data.eligibility.hi || '';
      } else {
        eligibilityValue = String(data.eligibility || '');
      }
      
      let procedureValue = '';
      let procedureMap: Record<string, string> | undefined;
      
      if (typeof data.procedure === 'object' && data.procedure !== null) {
        procedureMap = data.procedure as Record<string, string>;
        procedureValue = data.procedure.en || data.procedure.hi || '';
      } else {
        procedureValue = String(data.procedure || '');
      }
      
      let benefitsValue = '';
      let benefitsMap: Record<string, string> | undefined;
      
      if (typeof data.benefits === 'object' && data.benefits !== null) {
        benefitsMap = data.benefits as Record<string, string>;
        benefitsValue = data.benefits.en || data.benefits.hi || '';
      } else {
        benefitsValue = String(data.benefits || '');
      }
      
      return {
        id: docSnap.id,
        category: data.category,
        title: titleValue,
        description: descValue,
        eligibility: eligibilityValue,
        procedure: procedureValue,
        benefits: benefitsValue,
        officialLink: data.officialLink || '',
        launchDate: data.launchDate?.toDate(),
        lastUpdated: data.lastUpdated?.toDate(),
        isActive: data.isActive !== false,
        priority: data.priority || 0,
        stateAvailability: data.stateAvailability || [],
        isNew: data.isNew || false,
        _rawTitleMap: titleMap,
        _rawDescriptionMap: descMap,
        _rawEligibilityMap: eligibilityMap,
        _rawProcedureMap: procedureMap,
        _rawBenefitsMap: benefitsMap
      } as Scheme;
    }
    return null;
  }
}

export const firestoreService = new FirestoreService();